import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { memberProfileAPI, classInstanceAPI } from '../../services/api';
import { ClassInstance, MemberProfile } from '../../types';
import { formatTimeString, formatDate } from '../../utils/dateUtils';

interface RegisterMemberToInstanceDialogProps {
  open: boolean;
  onClose: () => void;
  classInstance: ClassInstance | null;
  onSuccess: () => void;
}

const RegisterMemberToInstanceDialog: React.FC<RegisterMemberToInstanceDialogProps> = ({
  open,
  onClose,
  classInstance,
  onSuccess
}) => {
  const [memberProfiles, setMemberProfiles] = useState<MemberProfile[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberProfile | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMembers, setLoadingMembers] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchMemberProfiles();
    }
  }, [open]);

  const fetchMemberProfiles = async () => {
    try {
      setLoadingMembers(true);
      setError(null);

      const response = await memberProfileAPI.getAllMemberProfiles();
      // Filter for active members only
      const activeMembers = (response.data || []).filter(
        (member: MemberProfile) => member.status === 'active'
      );
      setMemberProfiles(activeMembers);
    } catch (error: any) {
      console.error('Error fetching member profiles:', error);
      setError('Failed to fetch member profiles');
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleRegister = async () => {
    if (!selectedMember || !classInstance) return;

    try {
      setLoading(true);
      setError(null);

      await classInstanceAPI.registerMemberToInstance(classInstance.id, {
        memberProfileId: selectedMember.id,
        notes
      });

      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error registering member to instance:', error);
      setError(error.response?.data?.message || 'Failed to register member');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedMember(null);
    setNotes('');
    setError(null);
    onClose();
  };

  if (!classInstance) return null;

  return (
    <Dialog 
      open={open} 
      onClose={!loading ? handleClose : undefined}
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AddIcon sx={{ mr: 1 }} />
          Register Member to Class Instance
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Class Instance Info */}
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 3 }}>
          <Typography variant="h6" gutterBottom>{classInstance.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDate(classInstance.sessionDate)} • {formatTimeString(classInstance.startTime)} - {formatTimeString(classInstance.endTime)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Venue: {classInstance.venue} • Instructor: {classInstance.instructor}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={`${classInstance.enrolledCount || 0}/${classInstance.capacity} enrolled`}
              size="small"
              color={((classInstance.enrolledCount || 0) / classInstance.capacity) >= 0.8 ? 'warning' : 'default'}
            />
            <Chip 
              label={classInstance.status}
              size="small"
              color="primary"
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Member Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1 }} />
            Select Member
          </Typography>
          
          {loadingMembers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Autocomplete
              value={selectedMember}
              onChange={(_, newValue) => setSelectedMember(newValue)}
              options={memberProfiles}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
              getOptionKey={(option) => option.id}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">
                      {option.firstName} {option.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.email} • {typeof option.membershipTier === 'object' ? option.membershipTier?.name : option.membershipTier || 'Standard'}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search members by name or email..."
                  variant="outlined"
                />
              )}
              fullWidth
              disabled={loading}
            />
          )}
        </Box>

        {/* Notes */}
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Notes (Optional)
          </Typography>
          <TextField
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            placeholder="Add any notes about this registration..."
            fullWidth
            disabled={loading}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleRegister}
          variant="contained"
          disabled={!selectedMember || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {loading ? 'Registering...' : 'Register Member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegisterMemberToInstanceDialog;